import { getCountryFlag } from '@home/_config/countries';
import { getImg } from '@home/_config/getImgs';
import {
  camelCase,
  isEmpty,
  orderBy,
  sortBy,
  upperFirst,
  find,
} from 'lodash-es';
import moment from 'moment';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import {
  getCardChart,
  xAxisHighLight,
} from '@/pages/dashboard/components/Chart/_config/plug';
import * as echarts from 'echarts';
import { getMetricInfoListUICommon } from '../pages/LiveDownload/utils';
import { getArraySort } from '@gp/utils/common';
import { resolveLiveTitleChannelColor } from './channelColors';

export const transformData = (originalData: any[]) => {
  if (isEmpty(originalData)) return [];
  // 按area_code分组
  const grouped = originalData?.reduce((acc, country) => {
    const areaCode = country.area_code;
    if (!acc[areaCode]) {
      acc[areaCode] = {
        text: country.area_name_en,
        textCN: country.area_name_ch,
        children: [],
        value: country.country_code,
      };
    }

    acc[areaCode].children.push({
      value: country.country_code,
      text: country.country_name_en,
      textCN: country.country_name_ch,
      icon: getCountryFlag(country?.country_code),
    });

    return acc;
  }, {});

  // 转换为数组形式
  return  getArraySort(Object.values(grouped), 'string', 'asc', 'text');
};

export const transformDataSource = ({ dataSource, mapKey, metricTipsData }) => {
  if (isEmpty(dataSource)) return [];
  // 1. 首先按照mapKey的顺序对dataSource进行排序
  const orderedDataSource = orderBy(
    dataSource,
    [
      (item) => {
        // 获取当前metric在mapKey中的索引作为排序依据
        const keyIndex = Object.keys(mapKey).indexOf(item.metric);
        return keyIndex >= 0 ? keyIndex : Infinity; // 不在mapKey中的排最后
      },
    ],
    ['asc'], // 升序排列
  );
  return orderedDataSource
    .map((item) => {
      const mapItem = mapKey[item.metric];
      if (!mapItem) return null; // 如果mapKey中没有对应的metric则跳过
      const {
        definition = '-',
        source = '-',
        update_frequency = '-',
      } = metricTipsData?.find((task) => task.metric === item?.metric) || {};
      const data = {
        definition,
        source,
        updateCadence: update_frequency,
      };
      const tips = getMetricInfoListUICommon(data);
      return {
        titleEn: mapItem.textEn,
        titleCn: mapItem.textCn,
        isIconTips: true,
        definition,
        source,
        updateTime: update_frequency,
        formatRule: 'db_bmk00',
        value: item?.value?.toString(), // 转换为字符串
        tipsCn: tips, // 默认值
        tipsEn: tips, // 默认值
        className: 'dbs-metrics-card-with-rank__left',
        AWidth: 450,
        dodData: [
          {
            titleEn: 'MOM',
            titleCn: 'MOM',
            formatRule: 'absPercentInt',
            extendFormatRule: 'thousandsInit',
            value: (item?.mom?.percent
              ? item.mom.percent / 100
              : '-'
            )?.toString(),
            hideBubbleContent: true,
          },
          {
            titleEn: 'YOY',
            titleCn: 'YOY',
            formatRule: 'absPercentInt',
            extendFormatRule: 'thousandsInit',
            value: (item?.yoy?.percent
              ? item?.yoy?.percent / 100
              : '-'
            )?.toString(),
            hideBubbleContent: true,
          },
        ],
        key: item.metric,
        metricType: mapItem.metricType,
      };
    })
    .filter(Boolean); // 过滤掉null值
};

export const getMetricDataByKey = (data: any[], key: string) => {
  if (isEmpty(data)) return [];
  const foundItem = data?.find((item) => item?.metric === key);
  return foundItem?.timeline || [];
};

// timeline数据为MetricsCardTrendChart需要的格式
export const transformTimelineData = (
  timeline: Array<{
    date: string;
    value: number;
    yoy: number;
    mom: number;
    percent: number;
  }>,
) => {
  if (isEmpty(timeline)) return [];
  return timeline?.map((item) => ({
    date: item?.date,
    metrics: {
      current: {
        value: item?.value?.toString(),
        last_value: item?.yoy?.toString(), // 使用yoy作为对比值
      },
    },
  }));
};

export const getPlatFormData = (data, key) => {
  if (isEmpty(data)) return [];

  const { details } = data?.find((task) => task.metric === key) || {};
  const { value: totalValue } =
    details?.find((task) => task?.os === 'All Systems') || {};
  const sortList = sortBy(details, ['value'])
    .reverse()
    ?.filter((task) => task?.os !== 'All Systems')
    ?.slice(0, 6)
    ?.map((task) => {
      return {
        ...task,
        nameEn: task?.os,
        value: task?.value,
        subValue: task?.value / totalValue,
        icon: task?.icon || getImg(upperFirst(camelCase(task?.os))),
        // Color follows platform identity so revenue sort does not swap hues.
        color: resolveLiveTitleChannelColor(task?.os || ''),
      };
    });

  return sortList;
};

/**
 * 检测平台类型
 * @param {Array<string>} platforms - 平台数组，如 ["PC", "PS", "steam"]
 * @returns {string} 返回'all platforms'或命中的平台字符串拼接
 */
export function detectPlatforms(platforms) {
  if (isEmpty(platforms)) return [];
  // 定义目标平台类型（不区分大小写）
  const targetPlatforms = ['mobile', 'pc', 'console'];

  // 将输入平台数组转换为小写以便比较
  const lowerPlatforms = platforms.map((p) => p.toLowerCase());

  // 检查是否包含所有目标平台
  const hasAll = targetPlatforms.every((target) =>
    lowerPlatforms.includes(target),
  );

  if (hasAll) {
    return 'all platforms';
  }

  // 找出命中的平台
  const matched = targetPlatforms.filter((target) =>
    lowerPlatforms.includes(target),
  );

  // 返回命中的平台字符串拼接
  return matched.join(', ');
}

export const configFunc = (option: any) => {
  let opt = option;
  opt.yAxis.position = 'right';
  opt.yAxis.splitLine.show = false;
  opt.yAxis.splitNumber = 2;
  opt.xAxis[0].axisTick.show = false;
  opt.xAxis[0].axisLine.show = false;
  opt.series = opt?.series?.map((item: any, i: number) => ({
    ...item,
    areaStyle: {
      opacity: 0.25,
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: i === 0 ? '#7F6FF1' : '#69D19C',
        },
        {
          offset: 1,
          color: '#FFF',
        },
      ]),
    },

    emphasis: {
      focus: 'series',
    },
  }));

  opt = xAxisHighLight(opt, {
    lineStyle: {
      type: 'solid',
    },
    formatter: (params: any) => moment(params?.value).format('YYYY-MM'),
    symbolSize: 8,
    fontSize: 11,
  });
  opt.grid = {
    x: 15,
    y: 12,
    x2: 2,
    y2: 0,
    containLabel: true,
  };
  // 设计稿大小是11
  opt.legend.show = false;
  opt.xAxis[0].axisLabel.fontSize = 11;
  opt.xAxis[0].axisLabel.color = '#BBBDCB';
  opt.yAxis.axisLabel.fontSize = 11;
  opt.yAxis.axisLabel.color = '#BBBDCB';
  opt.tooltip.confine = false; // 浮窗不限制在图表区域
  opt.tooltip.axisPointer.label.padding = [5, 1, 0, 1];
  opt.tooltip.position = function (point: number[], params: any, dom: any, rect: any, size: { viewSize: number[]; contentSize: number[]; }) {
    // 获取 tooltip 的宽度和高度
    const tooltipWidth = size.contentSize[0];
    // 计算初始 tooltip 的 x 和 y 坐标
    let x = point[0];
    const y = point[1] - 100;

    // 如果 tooltip 的左侧超过图表左侧，则将其对齐方式改为右侧
    if (x - tooltipWidth < 0) {
      x = point[0];
    } else {
      x = point[0] - tooltipWidth;
    }
    return [x, y];
  };
  return opt;
};
